import isFunction from 'lodash/isFunction';

import { useLeadOrigin } from '~src/hooks/useLeadOrigin';

type EventAction =
  | 'automatic_quote_sent'
  | 'manual_quote_window_opened'
  | 'manual_quote_sent'
  | 'calculator_step_completed';

type ManualEvent =
  | 'estimate_page_go_to_booking'
  | 'estimate_page_go_to_calculator'
  | 'click_financing_toggle'
  | 'estimate_page_call_me'
  | 'estimate_page_booking_completed';

//Any custom parameters needs to be explicitly added to Google Analytics for it to be tracked
type GoogleAnalyticsParameter = 'lead_origin' | 'email' | 'step' | 'financing_enabled';

type GoogleAnalyticsParameters = Partial<Record<GoogleAnalyticsParameter, string | number | boolean | undefined>>;

type WindowWithTracking = typeof window & {
  gtag?: (...args: unknown[]) => void;
  fbq?: (...args: unknown[]) => void;
  dataLayer?: unknown[];
};

const trackEvent = (action: EventAction | ManualEvent, parameters?: GoogleAnalyticsParameters): void => {
  const { fbq, gtag, dataLayer } = (window as WindowWithTracking) ?? {};

  // GTM
  if (dataLayer && isFunction(dataLayer.push)) {
    dataLayer.push({ ...parameters, event: action });
  }

  // GTAG
  if (gtag && isFunction(gtag)) {
    gtag('event', action, parameters);
  }

  // META PIXEL
  if (fbq && isFunction(fbq)) {
    fbq('trackCustom', action, parameters);
  }
};

export const useAnalytics = () => {
  const { origin: lead_origin } = useLeadOrigin();

  return {
    trackEvent: (type: ManualEvent, parameters?: Omit<GoogleAnalyticsParameters, 'lead_origin'>) =>
      trackEvent(type, { ...parameters, lead_origin }),

    automaticQuoteSent: (email?: string, leadOrigin?: string) =>
      trackEvent('automatic_quote_sent', { email, lead_origin: leadOrigin ?? lead_origin }),

    quoteSent: (email?: string) => trackEvent('manual_quote_sent', { email, lead_origin }),

    quoteOpened: (email?: string) => trackEvent('manual_quote_window_opened', { email, lead_origin }),

    stepCompleted: (step: number) => trackEvent('calculator_step_completed', { step, lead_origin }),
  };
};
